import memoize from '../../memoize';
import {
  loadableMapToPlainObject,
  YearType,
} from '../../Utils';
import {
  getDataStatusSelector as getCPY_P_Selector,
  getYearsStatusSelector as getYearSelector,
} from '../fetchedData/countryProductYearByCountry';
import {
  getDataStatusSelector as getCYSelector,
} from '../fetchedData/countryYear';
import {
  getDataSelector as getProductLevelSelector,
  MappingType,
} from '../fetchedData/productLevelMapping';
import {
  getDataSelector as getMetadataSelector,
} from '../fetchedData/productMetadata';
import {
  getDataStatusSelector as getPYSelector,
} from '../fetchedData/productYear';
import {
  filterPopulationData as unmemoizedFilterPopulationData,
} from './deflatorUtils';

import {
  IRegistryEntry,
} from '../getWorkerEnhancer';
import {
  IWorkerRootState,
} from '../workerRootState';
import compute, {
  IComputationInput,
} from './compute';
import {
  checkForInvalidRoutingInput,
  IInputFromURLRouting,
  ISuccessfulMergeAction,
  IUIState,
  START_SUBSCRIBING,
  STOP_SUBSCRIBING,
} from './getReducer';
import {
  getInputFromURLRouting,
  getUIState,
  hasMergeAlreadyBeenComputed,
} from './reducer';

type IRelevantStorePart = {
  countryProductYearByCountry: IWorkerRootState['countryProductYearByCountry'],
  productLevelMapping: IWorkerRootState['productLevelMapping'],
  productYear: IWorkerRootState['productYear'],
  countryYear: IWorkerRootState['countryYear'],
  productMetadata: IWorkerRootState['productMetadata'],
} & IWorkerRootState['feasibility']['uiState'] & IInputFromURLRouting;

const getRelevantSubStore = (rootState: IWorkerRootState) => {
  const {
    countryProductYearByCountry, productLevelMapping,
    productYear, productMetadata, countryYear,
    feasibility: {uiState},
  } = rootState;
  const relevantPart: IRelevantStorePart = {
    countryProductYearByCountry, productLevelMapping,
    productYear, productMetadata, countryYear,
    ...uiState,
    ...getInputFromURLRouting(rootState),
  };
  return relevantPart;

};

/* Start of getComputationInput */
const getCPYData = getCPY_P_Selector();
const getPYData = getPYSelector();
const getLevelMapping = getProductLevelSelector();
const getMetadata = getMetadataSelector();
const getAvailableYears = getYearSelector();
const convertHigh2LowMapping = memoize(loadableMapToPlainObject);
const convertHigh2MidMapping = memoize(loadableMapToPlainObject);
const convertMetadata = memoize(loadableMapToPlainObject);
const getCYData = getCYSelector();
const filterPopulationData = memoize(unmemoizedFilterPopulationData);

const getComputationInput = (rootState: IWorkerRootState): IComputationInput => {
  const {
    detailLevel, yAxisMeasure, hideExports,
    deselectedCategories,
    svgHeight, svgWidth,
  } = getUIState(rootState);
  const retrieved = getInputFromURLRouting(rootState);
  const {productClass, year} = retrieved;
  const country = retrieved.country!;
  const queryLevel = retrieved.queryLevel;
  const yearRequest = {
    type: YearType.All as YearType.All,
  };
  const high2Low = getLevelMapping(rootState, {productClass, mappingType: MappingType.Hi2Low});
  const high2Mid = getLevelMapping(rootState, {productClass, mappingType: MappingType.Hi2Mid});
  const dataPY = getPYData(rootState, {productClass, year: yearRequest});

  const payload = {
    country, queryLevel, productClass, year: yearRequest,
    level: detailLevel,
  };
  const dataCPY_C = getCPYData(rootState, payload);
  const availableYears = getAvailableYears(rootState, payload);

  const metadata = getMetadata(rootState, {productClass});

  let result: IComputationInput;
  if (country !== undefined) {
    const dataCY = getCYData(rootState, {year: yearRequest});
    const filteredDataCY = filterPopulationData(dataCY, country);
    result = {
      svgWidth, svgHeight, detailLevel, productClass,
      high2Low: convertHigh2LowMapping(high2Low),
      high2Mid: convertHigh2MidMapping(high2Mid),
      metadata: convertMetadata(metadata),
      dataPY, availableYears,
      hideExports, dataCPY_C, year, yAxisMeasure,
      deselectedCategories,
      dataCY: filteredDataCY,
    };

  } else {
    throw new Error('This code path should never happen.');
  }

  return result;
};
/* End of getComputationInput */

const isURLRoutingInputValidForUI = (input: IInputFromURLRouting) => checkForInvalidRoutingInput(input).isValid;

const productSpaceRegistry: IRegistryEntry<
  IWorkerRootState,

  typeof START_SUBSCRIBING,
  typeof STOP_SUBSCRIBING,

  IInputFromURLRouting,
  IUIState,

  IRelevantStorePart,
  IComputationInput,
  ISuccessfulMergeAction
> = {
  startListeningActionName: START_SUBSCRIBING,
  stopListeningActionName: STOP_SUBSCRIBING,
  getRelevantSubStore,
  getComputationInput,
  isURLRoutingInputValidForUI,
  compute,
  getURLRoutingInputFromRootState: getInputFromURLRouting,
  getUIStateFromRootState: getUIState,
  hasMergeAlreadyBeenComputed,
};

export default productSpaceRegistry;
